<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mr-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Time Slots{{ planLabel ? ` - ${planLabel}` : "" }}</span>
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                solo
                rounded
                hide-details="auto"
                :loading="isFranchiseeLoading"
                v-model="franchiseeId"
                :items="franchiseeItems"
                label="Franchisee"
                placeholder="Select a Franchisee"
                prepend-inner-icon="mdi-store"
                @input="getTimeSlots()"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <p class="text-caption grey--text mt-3 mb-0" v-if="slotDuration">
            {{ slotDuration }}-min slots ({{ perWalkTime }} min walk +
            {{ slotDuration - perWalkTime }} min travel buffer) are
            auto-generated from this franchisee's Working from/Working Till
            ({{ workingHours.start_time }} - {{ workingHours.end_time }}). Use
            the toggle to disable a slot - disabled slots are not offered to
            customers on this plan.
          </p>
          <section v-if="isLoading" style="height: 200px">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </section>
          <v-alert
            v-else-if="errorMessage"
            type="warning"
            text
            class="mt-4 mb-0"
          >
            {{ errorMessage }}
            <div class="mt-2" v-if="franchiseeId">
              <v-btn
                small
                rounded
                color="primary"
                :to="`/franchisees/${encryptIt(franchiseeId)}`"
              >
                <v-icon left small>mdi-clock-edit-outline</v-icon>
                Set Working Hours
              </v-btn>
            </div>
          </v-alert>
          <v-simple-table v-else class="mt-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Sr No</th>
                  <th class="text-left">Start Time</th>
                  <th class="text-left">End Time</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody v-if="timeSlots.length != 0">
                <tr
                  v-for="(slot, slotIndex) in timeSlots"
                  :key="`plan_slot_${slot.id}`"
                >
                  <td>{{ slotIndex + 1 }}</td>
                  <td>{{ slot.start_time }}</td>
                  <td>{{ slot.end_time }}</td>
                  <td>
                    <v-chip
                      small
                      :color="slot.is_active ? 'green' : 'grey'"
                      text-color="white"
                    >
                      {{ slot.is_active ? "Active" : "Disabled" }}
                    </v-chip>
                  </td>
                  <td>
                    <v-switch
                      class="d-inline-block mb-0"
                      hide-details
                      dense
                      inset
                      :input-value="slot.is_active"
                      @change="toggleSlotActive(slot)"
                    ></v-switch>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="text-center">
                    {{
                      franchiseeId
                        ? 'No time slots yet. Set "Working from"/"Working Till" on this franchisee first.'
                        : !isFranchiseeLoading && franchiseeItems.length == 0
                        ? 'No franchisee has "Working from"/"Working Till" set yet.'
                        : "Select a franchisee to see this plan's time slots."
                    }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "PlanTimeSlotsPage",
  data() {
    return {
      planId: null,
      plan: {},
      franchiseeId: null,
      franchiseeItems: [],
      timeSlots: [],
      perWalkTime: 0,
      slotDuration: 0,
      workingHours: {},
      errorMessage: "",
      isLoading: false,
      isFranchiseeLoading: false,
    };
  },
  computed: {
    planLabel() {
      if (!this.plan.id) return "";
      return `${this.plan.no_of_dogs} Dog - ${this.plan.no_of_walks} Walk ${
        this.plan.tier || ""
      }`.trim();
    },
  },
  mounted() {
    this.planId = this.$route.query.plan_id
      ? parseInt(this.decryptIt(this.$route.query.plan_id))
      : null;
    this.getPlan();
    this.getFranchisees();
  },
  methods: {
    async getPlan() {
      if (!this.planId) return;
      await axios
        .get(`/plans/${this.encryptIt(this.planId)}`)
        .then((response) => {
          this.plan = response.data.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async getFranchisees() {
      this.isFranchiseeLoading = true;
      await axios
        .get("/franchisees", { params: { is_active: 1 } })
        .then((response) => {
          // Slots are generated from working hours, so a franchisee without
          // Working from/Working Till can't have any — leave it out.
          this.franchiseeItems = response.data.data
            .filter((franchisee) => franchisee.start_time && franchisee.end_time)
            .map((franchisee) => ({
              id: franchisee.id,
              text: `${franchisee.name} (${franchisee.start_time} - ${franchisee.end_time})`,
              value: franchisee.id,
            }));
          // Auto-select the first franchisee so the list isn't empty on open
          if (!this.franchiseeId && this.franchiseeItems.length) {
            this.franchiseeId = this.franchiseeItems[0].value;
            this.getTimeSlots();
          }
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isFranchiseeLoading = false;
    },
    async getTimeSlots() {
      if (!this.planId || !this.franchiseeId) return;
      this.isLoading = true;
      this.errorMessage = "";
      await axios
        .get("/plan_time_slots", {
          params: {
            plan_id: this.planId,
            franchisee_id: this.franchiseeId,
          },
        })
        .then((response) => {
          this.timeSlots = response.data.data;
          this.perWalkTime = response.data.per_walk_time;
          this.slotDuration = response.data.slot_duration;
          this.workingHours = response.data.working_hours || {};
        })
        .catch((error) => {
          this.timeSlots = [];
          this.slotDuration = 0;
          if (error.response && error.response.status === 422) {
            const data = error.response.data;
            this.errorMessage = data.message
              ? `${data.message} ${data["sub-title"] || ""}`.trim()
              : "This plan cannot generate time slots.";
          } else {
            console.error(error);
            this.isUnAuthorised();
          }
        });
      this.isLoading = false;
    },
    async toggleSlotActive(slot) {
      try {
        await axios.patch(`/plan_time_slots/${this.encryptIt(slot.id)}`, {
          is_active: !slot.is_active,
        });
        await this.getTimeSlots();
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>
